import * as yup from 'yup';
import { Row, RenderConfig, Column, TextInput, SelectInput, EMPTY_ARRAY, NumberInput } from 'commons-ui';
import _get from 'lodash/get';

const BOOLEAN_OPTIONS = [
	{ id: true, label: 'True' },
	{ id: false, label: 'False' }
];
const PARTNER_NAME_OPTIONS = [
	{ id: 'AMZ', label: 'AMZ - AMAZON' },
	{ id: 'FLP', label: 'FLP - FLIPKART' },
	{ id: 'MXP', label: 'MXP - MX PLAYER' }
];

export const validationSchemaCreate = yup.object().shape({
	isDisabled: yup.boolean().required(),
	maxRedemptions: yup.number().required(' Maximum Redemptions per code is a required field'),
	maxRedemptionsPerPerson: yup.number().when('allowUnlimitedRedemptionsPerPerson', {
		is: true,
		then: yup.number().nullable(),
		otherwise: yup.number().min(1, 'Maximum Redemptions Per Person should be atleast 1').required()
	}),
	validFrom: yup
		.number()
		.test({
			message: 'Valid from should be valid date',
			test: (value: any) => !Number.isNaN(new Date(value).getTime())
		})
		.test({
			message: 'Valid from should be greater than current time',
			test: (value: any) => new Date(value) > new Date()
		})
		.required('Valid from is a required field'),
	validTill: yup
		.number()
		.test({
			message: 'Valid till should be valid date',
			test: (value: any) => !Number.isNaN(new Date(value).getTime())
		})
		.test({
			message: 'Valid till should be greater than current time',
			test: (value: any) => new Date(value) > new Date()
		})
		.required('Valid till is a required field'),
	partnerName: yup
		.string()
		.matches(/[A-Za-z]+$/, 'Partner Name should only contain alphabets')
		.required('Partner Name is a required field'),
	quantity: yup.number().required('Quantity is a required field')
});

export const validationSchemaEdit = yup.object().shape({
	isDisabled: yup.boolean().required()
});
export const maxRedemptionPerPersonDependencyResolver = (props: any, dependencyValues: any) => {
	const allowUnlimitedRedemptionsPerPerson = _get(dependencyValues, '0', false);
	const isEditable = props.isEditable[props.mode];
	const { required, disabled } = props;

	if (allowUnlimitedRedemptionsPerPerson && isEditable && !disabled) {
		return { disabled: true };
	}

	if (allowUnlimitedRedemptionsPerPerson && required) {
		return { required: false };
	}

	if (!allowUnlimitedRedemptionsPerPerson && isEditable === disabled) {
		return { disabled: !isEditable };
	}

	if (!allowUnlimitedRedemptionsPerPerson && !required) {
		return { required: true };
	}

	return null;
};

export const FORM_FIELD_KEYS = {
	CID: 'cid',
	ISDISABLED: 'isDisabled',
	MAXREDEMPTIONS: 'maxRedemptions',
	MAX_REDEMPTIONS_PER_PERSON: 'maxRedemptionsPerPerson',
	ALLOW_UNLIMITED_REDEMPTIONS_PER_PERSON: 'allowUnlimitedRedemptionsPerPerson',
	VALIDFROM: 'validFrom',
	VALIDTILL: 'validTill',
	PARTNERNAME: 'partnerName',
	QUANTITY: 'quantity'
};

export const FORM_LAYOUT_CONFIG_CREATE = [
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.CID)]), new Column([new Row(FORM_FIELD_KEYS.ISDISABLED)])]),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.MAXREDEMPTIONS)])]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.ALLOW_UNLIMITED_REDEMPTIONS_PER_PERSON)]),
		new Column([new Row(FORM_FIELD_KEYS.MAX_REDEMPTIONS_PER_PERSON)])
	]),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.VALIDFROM)]), new Column([new Row(FORM_FIELD_KEYS.VALIDTILL)])]),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.PARTNERNAME)]), new Column([new Row(FORM_FIELD_KEYS.QUANTITY)])])
];

export const FORM_LAYOUT_CONFIG_EDIT = [
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.CID)]), new Column([new Row(FORM_FIELD_KEYS.ISDISABLED)])]),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.MAXREDEMPTIONS)])]),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.MAX_REDEMPTIONS_PER_PERSON)])]),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.VALIDFROM)]), new Column([new Row(FORM_FIELD_KEYS.VALIDTILL)])]),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.PARTNERNAME)]), new Column([new Row(FORM_FIELD_KEYS.QUANTITY)])])
];

export const FORM_FIELD_CONFIG_CREATE = {
	[FORM_FIELD_KEYS.CID]: new RenderConfig()
		.setName('cid')
		.setLabel('CID')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: false, edit: false }
		}),
	[FORM_FIELD_KEYS.ISDISABLED]: new RenderConfig()
		.setName('isDisabled')
		.setLabel('Is Disabled')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: BOOLEAN_OPTIONS
		}),
	[FORM_FIELD_KEYS.MAXREDEMPTIONS]: new RenderConfig()
		.setName('maxRedemptions')
		.setLabel('Max Redemptions Per Code')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: false }
		}),

	[FORM_FIELD_KEYS.ALLOW_UNLIMITED_REDEMPTIONS_PER_PERSON]: new RenderConfig()
		.setName('allowUnlimitedRedemptionsPerPerson')
		.setLabel('Allow Unlimited Redemptions Per Person')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: BOOLEAN_OPTIONS
		})
		.setToolTip('An user can apply coupon any number of times. Maximum Redemptions limit will be applied'),
	[FORM_FIELD_KEYS.MAX_REDEMPTIONS_PER_PERSON]: new RenderConfig()
		.setName('maxRedemptionsPerPerson')
		.setLabel('Maximum Redemptions Per Person')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: false },
			dependencies: ['allowUnlimitedRedemptionsPerPerson'],
			dependencyResolver: maxRedemptionPerPersonDependencyResolver
		})
		.setToolTip('Maximum usage per person'),
	[FORM_FIELD_KEYS.VALIDFROM]: new RenderConfig()
		.setName('validFrom')
		.setLabel('Valid From')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: false },
			inputType: 'datetime-local'
		}),
	[FORM_FIELD_KEYS.VALIDTILL]: new RenderConfig()
		.setName('validTill')
		.setLabel('Valid Till')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: false },
			inputType: 'datetime-local'
		}),
	[FORM_FIELD_KEYS.PARTNERNAME]: new RenderConfig()
		.setName('partnerName')
		.setLabel('Partner Name')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: false },
			options: PARTNER_NAME_OPTIONS
		}),
	[FORM_FIELD_KEYS.QUANTITY]: new RenderConfig()
		.setName('quantity')
		.setLabel('Quantity')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: false }
		})
};

export const FORM_FIELD_CONFIG_EDIT = {
	[FORM_FIELD_KEYS.CID]: new RenderConfig()
		.setName('cid')
		.setLabel('CID')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: false, edit: false }
		}),
	[FORM_FIELD_KEYS.ISDISABLED]: new RenderConfig()
		.setName('isDisabled')
		.setLabel('Is Disabled')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: BOOLEAN_OPTIONS
		}),

	[FORM_FIELD_KEYS.MAX_REDEMPTIONS_PER_PERSON]: new RenderConfig()
		.setName('maxRedemptionsPerPerson')
		.setLabel('Maximum Redemptions Per Person')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: false }
		})
		.setToolTip('Maximum usage per person'),
	[FORM_FIELD_KEYS.VALIDFROM]: new RenderConfig()
		.setName('validFrom')
		.setLabel('Valid From')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: false },
			inputType: 'datetime-local'
		}),

	[FORM_FIELD_KEYS.VALIDTILL]: new RenderConfig()
		.setName('validTill')
		.setLabel('Valid Till')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: false },
			inputType: 'datetime-local'
		}),
	[FORM_FIELD_KEYS.MAXREDEMPTIONS]: new RenderConfig()
		.setName('maxRedemptions')
		.setLabel('Max Redemptions Per Code')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: false }
		}),

	[FORM_FIELD_KEYS.PARTNERNAME]: new RenderConfig()
		.setName('partnerName')
		.setLabel('Partner Name')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: false },
			options: PARTNER_NAME_OPTIONS
		}),
	[FORM_FIELD_KEYS.QUANTITY]: new RenderConfig()
		.setName('quantity')
		.setLabel('Quantity')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: false }
		})
};

export const getFormDefaults = () => {
	return {
		isDisabled: false,
		validFrom: new Date().getTime(),
		validTill: new Date().getTime(),
		cid: ''
	};
};
